import {
    INotificationChannel,
    INotificationChannelUpdateDto,
    INotificationSubscriptionDto,
    NotificationSubscriptionType,
} from "@lib";
import { Modal } from "@core/components/alt-ui/modal";
import { Button, CheckBox, Control, Label, Panel, TextBox } from "@core/components/alt-ui/controls";
import { StringUtils } from "@/utils/string.utils";

export type NotificationSubscriptionsModalContext = {
    channel: INotificationChannel;
    saveHandler?: (channelId: string, dto: INotificationChannelUpdateDto) => Promise<boolean>;
};

abstract class Defaults {
    /** Заявка готова и ожидает выдачи. Через сколько дней напомнить. */
    public static OrderAwaitingPickupDelayDays = 2;

    /** Заявка готова и ожидает выдачи. В какое время напоминать. */
    public static OrderAwaitingPickupTimeOfDay = 10;

    /** Заявка готова и ожидает выдачи. Повторять ли напоминание каждый день. */
    public static OrderAwaitingPickupRepeatDaily = true;
}

export class NotificationSubscriptionsModal extends Modal<NotificationSubscriptionsModalContext> {
    private chEventOrderCreated!: CheckBox;
    private lbEventOrderCreatedDescription!: Label;

    private chEventSaleCreated!: CheckBox;
    private lbEventSaleCreatedDescription!: Label;

    private grEventOrderAwaitingPickup!: Panel;
    private chEventOrderAwaitingPickup!: CheckBox;
    private lbEventOrderAwaitingPickupDescription!: Label;
    private tbEventOrderAwaitingPickupDelayDays!: TextBox;
    private tbEventOrderAwaitingPickupTimeOfDay!: TextBox;
    private chEventOrderAwaitingPickupRepeatDaily!: CheckBox;

    private footer!: Panel;
    private btSave!: Button;

    private context: NotificationSubscriptionsModalContext | null = null;

    public constructor() {
        super("notification-subscriptions-modal", "Настройки уведомлений");
        this.initializeControls();
    }

    public show(context?: NotificationSubscriptionsModalContext): Promise<void> {
        this.context = context ?? null;
        this.initializeControls();

        if (context?.channel) {
            this.populateControls(context.channel);
        }

        return super.show();
    }

    protected initializeControls(): void {
        this.chEventOrderCreated = new CheckBox();
        this.chEventOrderCreated.id = "notification.subscriptions.order-created";
        this.chEventOrderCreated.class = "no-select";
        this.chEventOrderCreated.style = "margin-bottom: 0 !important";
        this.chEventOrderCreated.text = "Создана заявка";

        this.lbEventOrderCreatedDescription = new Label();
        this.lbEventOrderCreatedDescription.id = "notification.subscriptions.order-created.description";
        this.lbEventOrderCreatedDescription.class = "mb-0.25";
        this.lbEventOrderCreatedDescription.text = "Уведомление о том, что создана новая заявка";

        //

        this.chEventSaleCreated = new CheckBox();
        this.chEventSaleCreated.id = "notification.subscriptions.sale-created";
        this.chEventSaleCreated.class = "no-select";
        this.chEventSaleCreated.style = "margin-bottom: 0 !important";
        this.chEventSaleCreated.text = "Создана продажа";

        this.lbEventSaleCreatedDescription = new Label();
        this.lbEventSaleCreatedDescription.id = "notification.subscriptions.description";
        this.lbEventSaleCreatedDescription.class = "mb-0.25";
        this.lbEventSaleCreatedDescription.text = "Уведомление о том, что создана новая продажа";

        //

        this.grEventOrderAwaitingPickup = new Panel();

        this.chEventOrderAwaitingPickup = new CheckBox();
        this.chEventOrderAwaitingPickup.id = "notification.subscriptions.order-awaiting-pickup";
        this.chEventOrderAwaitingPickup.class = "no-select";
        this.chEventOrderAwaitingPickup.text = "Заявка готова и ожидает выдачи";
        this.chEventOrderAwaitingPickup.addValueChangedHandler((s, e) =>
            this.toggleOrderAwaitingPickupSettings(e.value),
        );
        this.grEventOrderAwaitingPickup.addControl(this.chEventOrderAwaitingPickup);

        this.lbEventOrderAwaitingPickupDescription = new Label();
        this.lbEventOrderAwaitingPickupDescription.id = "notification.subscriptions.order-awaiting-pickup.description";
        this.lbEventOrderAwaitingPickupDescription.class = "mb-0.75";
        this.lbEventOrderAwaitingPickupDescription.text = "Напоминание о заявках, которые находятся на этапе Готово";
        this.grEventOrderAwaitingPickup.addControl(this.lbEventOrderAwaitingPickupDescription);

        this.tbEventOrderAwaitingPickupDelayDays = new TextBox();
        this.tbEventOrderAwaitingPickupDelayDays.id = "notification.subscriptions.order-awaiting-pickup.delay-days";
        this.tbEventOrderAwaitingPickupDelayDays.class = "ml-2 mb-0.5";
        this.tbEventOrderAwaitingPickupDelayDays.label = "Напоминать через (дней)";
        this.tbEventOrderAwaitingPickupDelayDays.text = Defaults.OrderAwaitingPickupDelayDays.toString();
        this.tbEventOrderAwaitingPickupDelayDays.validation = "required|numeric|min_value:1|max_value:30";
        this.tbEventOrderAwaitingPickupDelayDays.visible = false;
        this.grEventOrderAwaitingPickup.addControl(this.tbEventOrderAwaitingPickupDelayDays);

        this.tbEventOrderAwaitingPickupTimeOfDay = new TextBox();
        this.tbEventOrderAwaitingPickupTimeOfDay.id = "notification.subscriptions.order-awaiting-pickup.time-of-day";
        this.tbEventOrderAwaitingPickupTimeOfDay.class = "ml-2 mb-0.5";
        this.tbEventOrderAwaitingPickupTimeOfDay.label = "Время напоминания (часы в текущем часовом поясе)";
        this.tbEventOrderAwaitingPickupTimeOfDay.text = Defaults.OrderAwaitingPickupTimeOfDay.toString();
        this.tbEventOrderAwaitingPickupTimeOfDay.validation = "required|numeric|min_value:0|max_value:23";
        this.tbEventOrderAwaitingPickupTimeOfDay.visible = false;
        this.grEventOrderAwaitingPickup.addControl(this.tbEventOrderAwaitingPickupTimeOfDay);

        this.chEventOrderAwaitingPickupRepeatDaily = new CheckBox();
        this.chEventOrderAwaitingPickupRepeatDaily.id = "notification.subscriptions.order-awaiting-pickup.repeat-daily";
        this.chEventOrderAwaitingPickupRepeatDaily.class = "ml-2";
        this.chEventOrderAwaitingPickupRepeatDaily.text = "Повторять напоминание каждый день";
        this.chEventOrderAwaitingPickupRepeatDaily.value = Defaults.OrderAwaitingPickupRepeatDaily;
        this.chEventOrderAwaitingPickupRepeatDaily.visible = false;
        this.grEventOrderAwaitingPickup.addControl(this.chEventOrderAwaitingPickupRepeatDaily);

        //

        this.btSave = new Button();
        this.btSave.id = "notification.subscriptions.save";
        this.btSave.text = "Сохранить";
        this.btSave.addClickHandler(() => this.save());

        this.footer = new Panel();
        this.footer.class = "flex justify-end mt-2";
        this.footer.addControl(this.btSave);
    }

    private populateControls(channel: INotificationChannel): void {
        this.chEventOrderCreated.value = !!channel.subscriptions?.find(
            s => s.type === NotificationSubscriptionType.OrderCreated,
        );

        this.chEventSaleCreated.value = !!channel.subscriptions?.find(
            s => s.type === NotificationSubscriptionType.SaleCreated,
        );

        this.populateOrderAwaitingPickupControls(channel);
    }

    private populateOrderAwaitingPickupControls(channel: INotificationChannel): void {
        const event = channel.subscriptions?.find(s => s.type === NotificationSubscriptionType.OrderAwaitingPickup);
        this.chEventOrderAwaitingPickup.value = !!event;

        if (!event) {
            return;
        }

        const delayDays =
            event.params?.find(p => p.name === "delayDays")?.value ?? Defaults.OrderAwaitingPickupDelayDays;
        this.tbEventOrderAwaitingPickupDelayDays.text = delayDays.toString();

        const timeOfDay = event.params?.find(p => p.name === "timeOfDay")?.value;
        //const timeOfDayHours = timeOfDay ? new Date(timeOfDay).getHours() : Defaults.OrderAwaitingPickupTimeOfDay;
        this.tbEventOrderAwaitingPickupTimeOfDay.text = timeOfDay.toString();

        const repeatDaily =
            event.params?.find(p => p.name === "repeatDaily")?.value ?? Defaults.OrderAwaitingPickupRepeatDaily;
        this.chEventOrderAwaitingPickupRepeatDaily.value = repeatDaily;
    }

    private toggleOrderAwaitingPickupSettings(checked: boolean): void {
        this.tbEventOrderAwaitingPickupDelayDays.visible = checked;
        this.tbEventOrderAwaitingPickupTimeOfDay.visible = checked;
        this.chEventOrderAwaitingPickupRepeatDaily.visible = checked;
    }

    public get controls(): Control[] {
        return [
            this.chEventOrderCreated,
            this.lbEventOrderCreatedDescription,

            this.chEventSaleCreated,
            this.lbEventSaleCreatedDescription,

            this.grEventOrderAwaitingPickup,

            this.footer,
        ];
    }

    private async cancel(): Promise<void> {
        this.hide();
    }

    private async save(): Promise<void> {
        const valid = await this.validate();

        if (!valid || !this.context?.saveHandler) {
            return;
        }

        const subscriptions: INotificationSubscriptionDto[] = [];

        this.addSubscriptionOrderCreated(subscriptions);
        this.addSubscriptionSaleCreated(subscriptions);
        this.addSubscriptionOrderAwaitingPickup(subscriptions);

        const dto: INotificationChannelUpdateDto = {
            subscriptions: subscriptions,
        };

        const result = await this.context.saveHandler(this.context.channel.id, dto);

        if (result) {
            this.hide(result);
        }
    }

    private addSubscriptionOrderCreated(subscriptions: INotificationSubscriptionDto[]): void {
        if (this.chEventOrderCreated.value) {
            subscriptions.push({
                type: NotificationSubscriptionType.OrderCreated,
            });
        }
    }

    private addSubscriptionSaleCreated(subscriptions: INotificationSubscriptionDto[]): void {
        if (this.chEventSaleCreated.value) {
            subscriptions.push({
                type: NotificationSubscriptionType.SaleCreated,
            });
        }
    }

    private addSubscriptionOrderAwaitingPickup(subscriptions: INotificationSubscriptionDto[]): void {
        if (!this.chEventOrderAwaitingPickup.value) {
            return;
        }

        const delayDays =
            StringUtils.integerStringToNumber(this.tbEventOrderAwaitingPickupDelayDays.text) ??
            Defaults.OrderAwaitingPickupDelayDays;

        const timeOfDayHours =
            StringUtils.integerStringToNumber(this.tbEventOrderAwaitingPickupTimeOfDay.text) ??
            Defaults.OrderAwaitingPickupTimeOfDay;

        const timeOfDayUtc = new Date();
        timeOfDayUtc.setHours(timeOfDayHours);
        timeOfDayUtc.setMinutes(0);
        timeOfDayUtc.setSeconds(0);
        timeOfDayUtc.setMilliseconds(0);

        const timeOfDayOffset = timeOfDayUtc.getTimezoneOffset();

        subscriptions.push({
            type: NotificationSubscriptionType.OrderAwaitingPickup,
            params: [
                { name: "delayDays", value: delayDays },
                { name: "timeOfDay", value: timeOfDayHours },
                { name: "timeOfDayOffset", value: timeOfDayOffset },
                { name: "timeOfDayUtc", value: timeOfDayUtc },
                { name: "repeatDaily", value: this.chEventOrderAwaitingPickupRepeatDaily.value },
            ],
        });
    }
}
